import { InventoryPrintMode } from '../components/ProductAdvancedSearch'
import { CoinInfo } from '../context/coin_context'
import {
    BarcodeTypeEnum,
    IvaEnum,
    LoadLoteInfo,
    LoadSerialInfo,
    LoadStockInfo,
    NotFoundProductFromExcel,
    ProductFromExcel,
    ProductHistoricInfo,
    ProductInfo,
    ProductInLoadLote,
    ProductInLoadSerial,
} from '../context/product_context'
import { TransferStockInfo } from '../pages/Products/TransferStock'
import { ListReturn } from '../utils/maks'
import { BranchApiData, parseBranch } from './branch'
import { ClientApiData, parseClient } from './client'
import { EmployeeApiData, parseEmployee } from './employee'
import { get, post, put } from './generic'
import { NoteApiData, parseNote } from './note'
import { parseProductInDelivery, ProductInDeliveryApiData } from './sale'
import { parseSupplier, SupplierApiData } from './supplier'
import { parseVehicle, VehicleApiData } from './vehicle'

export class BarcodeAlreadyInUseError extends Error {
    constructor(
        public readonly barcodes: string[],
        public readonly type: number
    ) {
        super()
    }
}

export interface CreateProductInterface {
    nome: string
    stock_minimo: number | null
    stock_ideal: number | null
    stock_atual?: number | null
    aplicacao: string | null
    observacao: string | null
    id_grupo: number | null
    id_sub_grupo: number | null
    id_marca: number | null
    id_modelo: number | null
    path_imagem: string | null
    valor_iva: number
    proporcao_iva?: number
    flag_controlar_estoque: number
    flag_sem_estoque: number
    flag_imprimir_fatura: number
    flag_ativo: number
    flag_autosoma: number
    flag_autopreco: number
    codigo_barras: string[]
    codigo_interno: string[]
    unit_measurement: string
    preço: {
        valor: number
        quantidade: number
    }[]
    preço_nome: {
        valor: number
        porc_lucro: number
        nome: string
        clientes: number[]
    }[]
    valor_compra: number
    porc_lucro: number
    id_fornecedor: number | null
    id_filial_product?: number | null
    id_produto_fatura: number | null
    flag_iva_obrigatorio: number
    preco_cif?: number
    ncm: string | null
    id_moeda: number
    peso: number
    flag_sem_comissao: number
    localizations: {
        valor: string
        id_filial: number | null
    }[]
    productsStockAfterOperation: {
        id_produto_carga: number
        quantidade_produto: number
    }[]
    filiais: number[]
}

export interface UpdateProductInterface
    extends Partial<CreateProductInterface> {
    id_produto: number
    flag_deletado?: number
}

export interface ListProductInterface {
    skip?: number
    take?: number
    search?: string | null
    groups?: number[] | null
    subgroups?: number[] | null
    brands?: number[] | null
    models?: number[] | null
    id_fornecedor?: number[] | null
    stockLevel?: number | null
    id_filial_filter?: number
    dontCount?: boolean
    orderBy?: 'name' | 'localization' | 'price'
}

export interface MassEditionInterface {
    filters: ListProductInterface
    fields: Partial<CreateProductInterface>
}

export interface GenerateInventoryInterface {
    search?: string
    groups?: number[] | null
    subgroups?: number[] | null
    brands?: number[] | null
    models?: number[] | null
    id_fornecedor?: number[] | null
    stockLevel?: number | null
    id_filial_filter?: number
    selectedFilters: Record<string, string>
    orderBy?: 'name' | 'localization' | 'price'
    inventoryMode?: InventoryPrintMode
    productIds?: number[]
    docType: 'pdf' | 'csv' | 'txt' | 'legal'
}

export interface ListProductHistoricInterface {
    id_produto?: number[]
    skip?: number
    take?: number
    id_cliente?: number[]
    tipo?: string
    data_inicial?: string
    data_final?: string
    selectedFilters?: Record<string, string>
    id_funcionario?: number[]
    id_filial?: number[]
    id_usuario?: number[]
}

export interface ProductApiData {
    nome: string
    stock_minimo: string | null
    stock_ideal: string | null
    stock_atual: string | null
    aplicacao: string | null
    observacao: string | null
    id_produto: number
    id_grupo: number | null
    id_sub_grupo: number | null
    id_marca: number | null
    id_modelo: number | null
    path_imagem: string | null
    iva_value: string
    proporcao_iva: number | string | null
    flag_stock_control: number
    flag_no_stock: number
    flag_print: number
    flag_active: number
    flag_autosoma: number
    flag_autopreco: number
    barcode: {
        id_codigo_barra: number
        codigo: string
        id_produto: number
        flag_tipo_codigo: number
    }[]
    unit_measurement: string
    product_price: {
        valor_venda: string
        porc_lucro: string
        quantidade: string | null
        nome: string | null
        id_produto: number
        id_valor_produto: number
        clienteValorProdutos: {
            cliente: ClientApiData
        }[]
    }[]
    valor_compra: string
    preco_cif: string
    preco_compra_medio: string
    preco_cif_medio: string
    porc_lucro: string
    flag_iva_obrigatorio: number
    flag_sem_comissao: number
    weight?: number
    estoqueFiliais?: {
        id_estoque_filial: number
        id_produto: number
        id_filial: number
        estoque: string
        flag_tipo_estoque: number
    }[]
    fornecedor: SupplierApiData | null
    filial: BranchApiData | null
    localizacoesProduto: {
        valor: string
        id_filial?: number
    }[]
    produtosCargaEstoquePosOperacao: {
        produtoCarga: ProductApiData
        quantidade_produto: string
    }[]
    produtoFatura: ProductApiData | null
    ncm: string | null
    id_moeda: number
    peso: string
    filiais: {
        filial?: BranchApiData
    }[]
}

export interface ProductHistoricApiData {
    id_operacao: number
    data: string
    hora: string
    valor: string
    quantidade: string
    tipo: 'entrada' | 'saida' | 'carga_estoque' | 'transferencia_estoque'
    flag_horario_verao: number
    nome_agente: string | null
    nome_filial: string | null
    nome_produto: string
    id_moeda: number
    id_moeda_estrangeira: number | null
    cambio: string | null
    valor_moeda_estrangeira: string | null
    flag_cambio_reverso: number | null
    preco_cif: string
    preco_compra_medio: string
    preco_cif_medio: string
    nome_funcionario: string | null
}

export interface LoadStockInterface {
    products: {
        id_produto: number
        quantidade_produto: number
        quantidade_produto_negativo: number
    }[]
    id_filial_master?: number | null
    anotacao?: string | null
}

export interface CancelLoadStockInterface {
    id_carga_estoque: number
}

export interface ListLoadStockInterface {
    skip: number
    take: number
    id_filial?: number[]
    data_inicial?: string
    data_final?: string
}

export interface ListLoadSerialInterface {
    skip: number
    take: number
    id_filial?: number[]
}

export interface ListLoadSerialProductInterface {
    skip?: number
    take?: number
    id_filial?: number[]
    id_produto?: number[]
    flag_entregue?: number
    search?: string
}

export interface CreateLoadSerialInterface {
    products: {
        id_produto: number
        serial: string
    }[]
    id_compra: number | null
    id_filial_master?: number | null
}

export interface UpdateLoadSerialProduct {
    id_produto_serial: number
    flag_deletado?: number
}

export interface ListLoadLoteInterface {
    skip: number
    take: number
    id_filial?: number[]
}

export interface ListLoadLoteProductInterface {
    skip?: number
    take?: number
    id_filial?: number[]
    id_produto?: number[]
    search?: string
}

export interface CreateLoadLoteInterface {
    products: {
        id_produto: number
        lote: string
        quantidade: number
        data_vencimento?: string | null
    }[]
    id_compra: number | null
    id_filial_master?: number | null
}

export interface UpdateLoadLoteProduct {
    id_produto_lote: number
    flag_deletado?: number
}

export interface LoadStockApiData {
    id_carga_estoque: number
    data: string
    hora: string
    produtosCargaEstoque: {
        id_produto_carga_estoque: number
        id_produto: number
        produto: ProductApiData
        quantidade_produto: number
        quantidade_produto_negativo: number
    }[]
    flag_horario_verao: number
    filial: BranchApiData | null
    usuario: {
        nome: string
    } | null
    caixa: {
        id_caixa: number
        nome: string
    } | null
    anotacao: string | null
    flag_status: number
}

export interface ProductInLoadSerialApiData {
    id_produto_serial: number
    id_produto: number
    produto: ProductApiData
    serial: string
    flag_entregue: number
    cargaSerial?: LoadSerialApiData
    produtosEntrega?: ProductInDeliveryApiData[]
}

export interface LoadSerialApiData {
    id_carga_serial: number
    data: string
    hora: string
    produtosCargaSerial: ProductInLoadSerialApiData[]
    flag_horario_verao: number
    id_compra: number | null
    filial: BranchApiData | null
    usuario: {
        nome: string
    } | null
    caixa: {
        id_caixa: number
        nome: string
    } | null
}

export interface ProductInLoadLoteApiData {
    id_produto_lote: number
    id_produto: number
    produto: ProductApiData
    lote: string
    quantidade: number
    quantidade_entregue: number
    data_vencimento: string | null
    cargaLote?: LoadLoteApiData
    produtosEntrega?: ProductInDeliveryApiData[]
}

export interface LoadLoteApiData {
    id_carga_lote: number
    data: string
    hora: string
    produtosCargaLote: ProductInLoadLoteApiData[]
    flag_horario_verao: number
    id_compra: number | null
    filial: BranchApiData | null
    usuario: {
        nome: string
    } | null
    caixa: {
        id_caixa: number
        nome: string
    } | null
}

export interface CraeteTransferStockInterface {
    products: {
        id_produto: number
        quantidade_produto: number
    }[]
    data_chegada: string
    data_saida: string
    hora_chegada: string
    hora_saida: string
    id_filial_origem: number
    id_filial_destino?: number
    id_cliente?: number
    flag_tipo_destino: number
    id_funcionario: number
    id_veiculo: number
    distancia_estimada: number
    flag_descontar_estoque: number
    id_venda: number | null
    anotacao: string | null
}

export interface CancelTransferStockInterface {
    id_transferencia_estoque: number
}

export interface UpdateTransferStockInterface {
    id_transferencia_estoque: number
    use_legal_note?: boolean
}

export interface ListTransferStock {
    skip?: number
    take?: number
    search?: string
    id_venda?: number[]
    loadFullRelations?: boolean
    data_inicial?: string
    data_final?: string
    id_cliente?: number
    id_filial_origem?: number
    id_filial_destino?: number
    flag_tipo_destino?: number
}

export interface TransferStockApiData {
    id_transferencia_estoque: number
    data: string
    hora: string
    data_chegada: string
    data_saida: string
    hora_chegada: string
    hora_saida: string
    id_filial_destino: number
    id_filial_origem: number
    id_funcionario: number
    id_veiculo: number
    produtosTransferenciaEstoque: {
        id_produto_transferencia_estoque: number
        id_produto: number
        produto: ProductApiData
        quantidade_produto: string
    }[]
    flag_horario_verao: number
    filialOrigem: BranchApiData
    filialDestino: BranchApiData | null
    cliente: ClientApiData | null
    flag_tipo_destino: number
    funcionario: EmployeeApiData
    veiculo: VehicleApiData
    usuario: {
        nome: string
    } | null
    caixa: {
        id_caixa: number
        nome: string
    } | null
    numero_fatura: string | null
    nota: NoteApiData | null
    distancia_estimada: string
    cdc_fatura?: string
    qrcode?: string
    status_fatura_eletronica?: number
    log_envio_fatura?: string
    flag_descontar_estoque: number
    id_venda: number | null
    flag_status: number
    anotacao: string | null
}

export interface GenerateTotalInventoryOutputInterface {
    id_filial: number
    id_moeda: number
    stock: number
    totalCost: number
    unidade_medida: number
}

export interface ProductFromExcelApiData {
    product: ProductApiData
    qnt: number
    price: number
}

export interface ProductFromExcelResult {
    products: ProductFromExcel[]
    notFoundProducts: NotFoundProductFromExcel[]
}

export function ParseProduct(
    data: ProductApiData,
    coins?: CoinInfo[]
): ProductInfo {
    return {
        aplication: data.aplicacao,
        barcodes: data.barcode?.filter(
            (b) => b.flag_tipo_codigo === BarcodeTypeEnum.Barcode
        ).length
            ? data.barcode
                  .filter((b) => b.flag_tipo_codigo === BarcodeTypeEnum.Barcode)
                  .map((barcode) => barcode.codigo)
            : [''],
        internalCodes: data.barcode?.filter(
            (b) => b.flag_tipo_codigo === BarcodeTypeEnum.Internal
        ).length
            ? data.barcode
                  .filter(
                      (b) => b.flag_tipo_codigo === BarcodeTypeEnum.Internal
                  )
                  .map((barcode) => barcode.codigo)
            : [''],
        buyPrice: Number(data.valor_compra),
        cifPrice: Number(data.preco_cif),
        avgBuyPrice: Number(data.preco_compra_medio),
        avgCifPrice: Number(data.preco_cif_medio),
        stock: Number(data.stock_atual),
        idealStock: data.stock_ideal ? Number(data.stock_ideal) : null,
        minStock: data.stock_minimo ? Number(data.stock_minimo) : null,
        image: data.path_imagem
            ? {
                  content: data.path_imagem,
                  file: new File([], data.path_imagem),
                  isBase64: false,
              }
            : null,
        name: data.nome,
        observation: data.observacao,
        porcentGain: Number(data.porc_lucro),
        iva:
            Number(data.iva_value) === 10
                ? IvaEnum.Dez
                : Number(data.iva_value) === 5
                  ? IvaEnum.Cinco
                  : IvaEnum.Exento,
        proporcaoIva:
            Number(data.proporcao_iva) > 0
                ? Number(data.proporcao_iva)
                : 100,
        showIva: Boolean(data.flag_print),
        sellWithoutStock: Boolean(data.flag_no_stock),
        selectedGroup: data.id_grupo ? { id: data.id_grupo, name: '' } : null,
        selectedSubGroup:
            data.id_sub_grupo && data.id_grupo
                ? { id: data.id_sub_grupo, name: '', groupId: data.id_grupo }
                : null,
        selectedMark: data.id_marca ? { id: data.id_marca, name: '' } : null,
        selectedModel:
            data.id_modelo && data.id_marca
                ? { id: data.id_modelo, name: '', markId: data.id_marca }
                : null,
        unitType: data.unit_measurement,
        salePrice: data.product_price
            ? data.product_price
                  .filter((price) => Number(price.quantidade) === 1)
                  .map((price) => Number(price.valor_venda))
                  .shift() ?? 0
            : 0,
        qntPrice: data.product_price
            ? data.product_price
                  .filter(
                      (price) =>
                          price.quantidade && Number(price.quantidade) > 1
                  )
                  .map((price) => {
                      return {
                          price: Number(price.valor_venda),
                          qnt: Number(price.quantidade),
                      }
                  })
            : [],
        namedPrice: data.product_price
            ? data.product_price
                  .filter((price) => price.nome)
                  .map((price) => {
                      return {
                          price: Number(price.valor_venda),
                          name: price.nome as string,
                          porcentGain: Number(price.porc_lucro),
                          clients: price.clienteValorProdutos
                              ? price.clienteValorProdutos.map((cvp) => ({
                                    ...parseClient(cvp.cliente),
                                    clientName: cvp.cliente.nome,
                                    name: '',
                                }))
                              : [],
                      }
                  })
            : [],
        id: data.id_produto,
        isAtive: Boolean(data.flag_active),
        autoSum: Boolean(data.flag_autosoma),
        autoPrice: Boolean(data.flag_autopreco),
        weight: data.weight,
        branchStocks: data.estoqueFiliais?.map((estoqueFilial) => ({
            id: estoqueFilial.id_estoque_filial,
            branchId: estoqueFilial.id_filial,
            stock: Number(estoqueFilial.estoque),
            type: estoqueFilial.flag_tipo_estoque,
            unitType: data.unit_measurement,
            idealStock: data.stock_ideal ? Number(data.stock_ideal) : null,
            minStock: data.stock_minimo ? Number(data.stock_minimo) : null,
        })),
        supplier: data.fornecedor
            ? parseSupplier(data.fornecedor, coins)
            : null,
        branch: data.filial ? parseBranch(data.filial) : null,
        localizations:
            data.localizacoesProduto?.map((lp) => ({
                value: lp.valor,
                branchId: lp.id_filial,
            })) ?? [],
        productsStockAfterOperation: data.produtosCargaEstoquePosOperacao?.map(
            (v) => ({
                product: ParseProduct(v.produtoCarga, coins),
                qnt: Number(v.quantidade_produto),
            })
        ),
        parsedName: data.nome
            .concat(data.observacao ? ` - ${data.observacao}` : '')
            .concat(data.aplicacao ? ` - ${data.aplicacao}` : ''),
        noteProduct: data.produtoFatura
            ? ParseProduct(data.produtoFatura, coins)
            : null, 
        obrigatoryIva: Boolean(data.flag_iva_obrigatorio),
        ncm: data.ncm,
        coin: coins?.find((c) => c.id === data.id_moeda),
        realWeight: Number(data.peso),
        withoutComission: Boolean(data.flag_sem_comissao),
        branches: data.filiais?.length
            ? data.filiais.map((f) =>
                  f.filial ? parseBranch(f.filial, coins) : null
              )
            : [null],
    }
}

export function parseProductHistoric(
    data: ProductHistoricApiData,
    coins: CoinInfo[]
): ProductHistoricInfo {
    return {
        id: 0,
        operationId: data.id_operacao,
        date: data.data.split('T')[0],
        hour: data.hora,
        value: Number(data.valor),
        qnt: Number(data.quantidade),
        type: data.tipo,
        isSummerTime: Boolean(data.flag_horario_verao),
        agentName: data.nome_agente,
        branchName: data.nome_filial,
        productName: data.nome_produto,
        coin: coins.find((c) => c.id === data.id_moeda),
        exchange: data.cambio
            ? {
                  amount: Number(data.valor_moeda_estrangeira),
                  coin: coins.find(
                      (c) => c.id === data.id_moeda_estrangeira
                  ) as CoinInfo,
                  exchange: Number(data.cambio),
                  flagReverseExchange: data.flag_cambio_reverso as number,
              }
            : null,
        cifPrice: Number(data.preco_cif),
        avgBuyPrice: Number(data.preco_compra_medio),
        avgCifPrice: Number(data.preco_cif_medio),
        employeeName: data.nome_funcionario,
    }
}

export function parseLoadStock(data: LoadStockApiData): LoadStockInfo {
    return {
        id: data.id_carga_estoque,
        account: {
            name: data.usuario?.nome,
        },
        cashier: {
            id: data.caixa?.id_caixa,
            name: data.caixa?.nome,
        },
        date: data.data,
        hour: data.hora,
        branch: data.filial ? parseBranch(data.filial) : null,
        isSummerTime: !!data.flag_horario_verao,
        products: data.produtosCargaEstoque.map((pce) => ({
            id: pce.id_produto_carga_estoque,
            qnt: pce.quantidade_produto,
            negativeQnt: pce.quantidade_produto_negativo,
            product: ParseProduct(pce.produto),
        })),
        annotation: data.anotacao,
        status: data.flag_status,
    }
}

export function parseLoadSerialProduct(
    pcs: ProductInLoadSerialApiData
): ProductInLoadSerial {
    return {
        id: pcs.id_produto_serial,
        serial: pcs.serial,
        isDelivered: Boolean(pcs.flag_entregue),
        product: pcs.produto ? ParseProduct(pcs.produto) : undefined,
        loadSerial: pcs.cargaSerial
            ? parseLoadSerial(pcs.cargaSerial)
            : undefined,
        productInDelivery: pcs.produtosEntrega?.map((pe) =>
            parseProductInDelivery(pe, [])
        ),
    }
}

export function parseLoadSerial(data: LoadSerialApiData): LoadSerialInfo {
    return {
        id: data.id_carga_serial,
        account: {
            name: data.usuario?.nome,
        },
        cashier: {
            id: data.caixa?.id_caixa,
            name: data.caixa?.nome,
        },
        date: data.data,
        hour: data.hora,
        branch: data.filial ? parseBranch(data.filial) : null,
        isSummerTime: !!data.flag_horario_verao,
        products: data.produtosCargaSerial?.map(parseLoadSerialProduct),
        buyId: data.id_compra,
    }
}

export function parseLoadLoteProduct(
    plc: ProductInLoadLoteApiData
): ProductInLoadLote {
    return {
        id: plc.id_produto_lote,
        lote: plc.lote,
        quantidade: Number(plc.quantidade),
        deliveredQnt: Number(plc.quantidade_entregue),
        expDate: plc.data_vencimento,
        product: plc.produto ? ParseProduct(plc.produto) : undefined,
        loadLote: plc.cargaLote ? parseLoadLote(plc.cargaLote) : undefined,
        productInDelivery: plc.produtosEntrega?.map((pe) =>
            parseProductInDelivery(pe, [])
        ),
    }
}

export function parseLoadLote(data: LoadLoteApiData): LoadLoteInfo {
    return {
        id: data.id_carga_lote,
        account: {
            name: data.usuario?.nome,
        },
        cashier: {
            id: data.caixa?.id_caixa,
            name: data.caixa?.nome,
        },
        date: data.data,
        hour: data.hora,
        branch: data.filial ? parseBranch(data.filial) : null,
        isSummerTime: !!data.flag_horario_verao,
        products: data.produtosCargaLote?.map(parseLoadLoteProduct),
        buyId: data.id_compra,
    }
}

export function parseTransferStock(
    data: TransferStockApiData
): TransferStockInfo {
    return {
        id: data.id_transferencia_estoque,
        account: data.usuario
            ? {
                  name: data.usuario?.nome,
              }
            : null,
        cashier: data.caixa
            ? {
                  id: data.caixa?.id_caixa,
                  name: data.caixa?.nome,
              }
            : null,
        date: data.data,
        hour: data.hora,
        exitDate: new Date(`${data.data_saida} ${data.hora_saida}`),
        arrivalDate: new Date(`${data.data_chegada} ${data.hora_chegada}`),
        originBranch: data.filialOrigem ? parseBranch(data.filialOrigem) : null,
        destinyBranch: data.filialDestino
            ? parseBranch(data.filialDestino)
            : null,
        client: data.cliente ? parseClient(data.cliente) : null,
        destinationType: data.flag_tipo_destino,
        isSummerTime: !!data.flag_horario_verao,
        products:
            data.produtosTransferenciaEstoque?.map((pte) => ({
                id: pte.id_produto_transferencia_estoque,
                qnt: Number(pte.quantidade_produto),
                product: ParseProduct(pte.produto),
            })) ?? [],
        bill: data.numero_fatura,
        note: data.nota ? parseNote(data.nota) : null,
        employee: data.funcionario ? parseEmployee(data.funcionario) : null,
        vehicle: data.veiculo ? parseVehicle(data.veiculo) : null,
        estimatedDistance: Number(data.distancia_estimada),
        cdc: data.cdc_fatura,
        qrCode: data.qrcode,
        statusEletronicNote: data.status_fatura_eletronica,
        logEletronicNote: data.log_envio_fatura,
        discountStock: Boolean(data.flag_descontar_estoque),
        saleId: data.id_venda,
        status: data.flag_status,
        annotation: data.anotacao,
    }
}

export async function createProduct(
    infos: CreateProductInterface
): Promise<number> {
    try {
        const res = await post('product', infos, {
            useRecaptcha: true,
        })
        return res.data.data.product.id_produto
    } catch (error: any) {
        if (
            error?.status === 409 &&
            error?.message.includes('Barcodes already in use')
        ) {
            const barcodes = error.message.split(': ')[1].split(',')
            throw new BarcodeAlreadyInUseError(
                barcodes,
                BarcodeTypeEnum.Barcode
            )
        }

        if (
            error?.status === 409 &&
            error?.message.includes('Internalcodes already in use')
        ) {
            const barcodes = error.message.split(': ')[1].split(',')
            throw new BarcodeAlreadyInUseError(
                barcodes,
                BarcodeTypeEnum.Internal
            )
        }
        throw error
    }
}

export async function updateProduct(
    infos: UpdateProductInterface
): Promise<void> {
    try {
        await put('product', infos, {
            useRecaptcha: true,
        })
    } catch (error: any) {
        if (
            error?.status === 409 &&
            error?.message.includes('Barcodes already in use')
        ) {
            const barcodes = error.message.split(': ')[1].split(',')
            throw new BarcodeAlreadyInUseError(
                barcodes,
                BarcodeTypeEnum.Barcode
            )
        }

        if (
            error?.status === 409 &&
            error?.message.includes('Internalcodes already in use')
        ) {
            const barcodes = error.message.split(': ')[1].split(',')
            throw new BarcodeAlreadyInUseError(
                barcodes,
                BarcodeTypeEnum.Internal
            )
        }
        throw error
    }
}

export async function massEditProducts(
    infos: MassEditionInterface
): Promise<void> {
    try {
        await post('product/mass-edit', infos, {
            useRecaptcha: true,
        })
    } catch (error: any) {
        if (
            error?.status === 409 &&
            error?.message.includes('Barcodes already in use')
        ) {
            const barcodes = error.message.split(': ')[1].split(',')
            throw new BarcodeAlreadyInUseError(
                barcodes,
                BarcodeTypeEnum.Barcode
            )
        }

        if (
            error?.status === 409 &&
            error?.message.includes('Internalcodes already in use')
        ) {
            const barcodes = error.message.split(': ')[1].split(',')
            throw new BarcodeAlreadyInUseError(
                barcodes,
                BarcodeTypeEnum.Internal
            )
        }
        throw error
    }
}

export async function listProducts(
    infos: ListProductInterface,
    coins: CoinInfo[]
): Promise<ListReturn<ProductInfo>> {
    const res = await post('product/list', infos)
    return {
        data: res.data.data.data.map((data: ProductApiData) =>
            ParseProduct(data, coins)
        ),
        count: res.data.data.count,
    }
}

export async function getProductById(
    id: number,
    coins: CoinInfo[]
): Promise<ProductInfo> {
    const res = await post('product/specific', {
        id_produto: id,
    })

    return ParseProduct(res.data.data, coins)
}

export async function generateInventory(
    data: GenerateInventoryInterface
): Promise<void> {
    const res = await post('product/inventory', data, {
        useRecaptcha: true,
    })
    return res.data.data
}

export async function createNewLoadStock(
    data: LoadStockInterface
): Promise<void> {
    await post('product/load-stock', data, {
        useRecaptcha: true,
    })
}
export async function cancelLoadStock(
    data: CancelLoadStockInterface
): Promise<void> {
    await post('product/load-stock/cancel', data, {
        useRecaptcha: true,
    })
}

export async function getTotalInventory(): Promise<
    GenerateTotalInventoryOutputInterface[]
> {
    const res = await get('product/total-inventory')
    return res.data.data
}

export async function listProductHistoric(
    data: ListProductHistoricInterface,
    coins: CoinInfo[]
): Promise<ListReturn<ProductHistoricInfo>> {
    const res = await post('product/list-historic', data)
    return {
        data: res.data.data.data.map(
            (data: ProductHistoricApiData, i: number) => ({
                ...parseProductHistoric(data, coins),
                id: i + 1,
            })
        ),
        count: Number(res.data.data.count),
    }
}

export async function generateProductHistoric(
    data: ListProductHistoricInterface
): Promise<void> {
    await post('product/list-historic/generate', data)
}

export async function listLoadStock(
    data: ListLoadStockInterface
): Promise<ListReturn<LoadStockInfo>> {
    const res = await post('product/load-stock/list', data)
    return {
        data: res.data.data.data.map(parseLoadStock),
        count: Number(res.data.data.count),
    }
}

export async function createTransferStock(
    data: CraeteTransferStockInterface
): Promise<void> {
    await post('product/transfer-stock', data, {
        useRecaptcha: true,
    })
}

export async function cancelTransferStock(
    data: CancelTransferStockInterface
): Promise<void> {
    await post('product/transfer-stock/cancel', data, {
        useRecaptcha: true,
    })
}

export async function updateTransferStock(
    data: UpdateTransferStockInterface
): Promise<void> {
    await put('product/transfer-stock', data, {
        useRecaptcha: true,
    })
}

export async function listTransferStock(
    data: ListTransferStock
): Promise<ListReturn<TransferStockInfo>> {
    const res = await post('product/transfer-stock/list', data)
    return {
        data: res.data.data.data.map(parseTransferStock),
        count: Number(res.data.data.count),
    }
}

export async function getTransferStockById(
    id: number
): Promise<TransferStockInfo> {
    const res = await get(`product/transfer-stock/${id}`)
    return parseTransferStock(res.data.data)
}

export async function listLoadSerial(
    data: ListLoadSerialInterface
): Promise<ListReturn<LoadSerialInfo>> {
    const res = await post('product/load-serial/list', data)
    return {
        data: res.data.data.data.map(parseLoadSerial),
        count: Number(res.data.data.count),
    }
}

export async function listLoadSerialProduct(
    data: ListLoadSerialProductInterface
): Promise<ListReturn<ProductInLoadSerial>> {
    const res = await post('product/serial/list', data)
    return {
        data: res.data.data.data.map(parseLoadSerialProduct),
        count: Number(res.data.data.count),
    }
}

export async function createLoadSerial(
    data: CreateLoadSerialInterface
): Promise<void> {
    await post('product/load-serial', data, {
        useRecaptcha: true,
    })
}

export async function updateLoadSerialProduct(
    data: UpdateLoadSerialProduct
): Promise<void> {
    await put('product/serial', data, {
        useRecaptcha: true,
    })
}

export async function listLoadLote(
    data: ListLoadLoteInterface
): Promise<ListReturn<LoadLoteInfo>> {
    const res = await post('product/load-lote/list', data)
    return {
        data: res.data.data.data.map(parseLoadLote),
        count: Number(res.data.data.count),
    }
}

export async function listLoadLoteProduct(
    data: ListLoadLoteProductInterface
): Promise<ListReturn<ProductInLoadLote>> {
    const res = await post('product/lote/list', data)
    return {
        data: res.data.data.data.map(parseLoadLoteProduct),
        count: Number(res.data.data.count),
    }
}

export async function createLoadLote(
    data: CreateLoadLoteInterface
): Promise<void> {
    await post('product/load-lote', data, {
        useRecaptcha: true,
    })
}

export async function updateLoadLoteProduct(
    data: UpdateLoadLoteProduct
): Promise<void> {
    await put('product/lote', data, {
        useRecaptcha: true,
    })
}

export async function generateRandomBarcode(
    type: number,
    getLastInsertedId?: boolean
): Promise<string> {
    const res = await get(
        `product/generate-random-barcode/${type}?getLastInsertedId=${
            getLastInsertedId ?? ''
        }`
    )
    return res.data.data
}

export async function loadProductsFromExcel(
    base64Excel: string,
    coins: CoinInfo[],
    id_filial_master?: number | null
): Promise<ProductFromExcelResult> {
    const res = await post(
        'product/load-products-from-excel',
        { base64Excel, id_filial_master },
        {
            useRecaptcha: true,
        }
    )

    return {
        products: res.data.data.products.map(
            (data: ProductFromExcelApiData) => ({
                product: ParseProduct(data.product, coins),
                qnt: data.qnt,
                price: data.price,
            })
        ),
        notFoundProducts: res.data.data.notFoundProducts,
    }
}

export interface CalculateSalePriceInputItem {
    id_produto: number
    preco_compra: number
    preco_cif: number
    quantidade_produto: number
}

export interface CalculateSalePriceOutputItem {
    id_produto: number
    preco_compra_medio: number
    preco_cif_medio: number
    finalBuyPrice: number
}

export async function calculateSalePrices(
    items: CalculateSalePriceInputItem[],
    idMoeda?: number
): Promise<CalculateSalePriceOutputItem[]> {
    const res = await post('product/calculate-sale-prices', {
        items,
        id_moeda: idMoeda,
    })
    return res.data.data.data
}
